import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getManager, getActiveManagers, getManagerByDomain } from '../../api/managerApi';
import './Login.css';   // ✅ 별도 CSS 파일 import

const isTrueValue = (value) => value === true || value === 1 || value === '1' || value === 'true';

const withCacheBuster = (src, version) => {
  if (!src) return '';
  const separator = src.includes('?') ? '&' : '?';
  return `${src}${separator}v=${version}`;
};

function Login() {
  const [form, setForm] = useState({ userid: '', password: '' });
  const [managers, setManagers] = useState([]);
  const [selectedComCode, setSelectedComCode] = useState('');
  const [loginInfo, setLoginInfo] = useState(null);
  const [imageVersion, setImageVersion] = useState(Date.now());
  const [authentikEnabled, setAuthentikEnabled] = useState(false);
  const [backendBaseUrl, setBackendBaseUrl] = useState('http://localhost:5000');
  const [domainManager, setDomainManager] = useState(null);
  const autoRedirectedComCodeRef = useRef('');
  const navigate = useNavigate();
  const { handleLogin } = useContext(AuthContext);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const domain = window.location.hostname;
    if (!domain) return;
    console.log(domain);
    getManagerByDomain(domain)
      .then((manager) => {
        if (!manager) return;
        setDomainManager(manager);
        setSelectedComCode(String(manager.M_ComCode || ''));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const rows = await getActiveManagers();
        const list = Array.isArray(rows) ? rows : [];
        setManagers(list);

        if (list.length === 0) return;

        const fallbackCode = String(list[0]?.M_ComCode || '');
        setSelectedComCode((prev) => {
          const hasPrev = list.some((m) => String(m?.M_ComCode || '') === String(prev || ''));
          if (hasPrev) return prev;
          return fallbackCode;
        });
      } catch (err) {
        console.error('회사 목록 조회 실패:', err);
      }
    };

    fetchManagers();
  }, []);

  useEffect(() => {
    if (!selectedComCode) return;

    const fetchLoginInfo = async () => {
      try {
        const manager = await getManager(selectedComCode);
        setLoginInfo(manager);
        setImageVersion(Date.now());
      } catch (err) {
        console.error('회사 로그인 정보 조회 실패:', err);
      }
    };

    fetchLoginInfo();
  }, [selectedComCode]);

  useEffect(() => {
    const fetchAuthentikConfig = async () => {
      try {
        const res = await axios.get('/api/auth/authentik/config');
        setAuthentikEnabled(Boolean(res.data?.enabled));
      } catch (err) {
        console.error('Authentik 설정 조회 실패:', err);
        setAuthentikEnabled(false);
      }
    };

    fetchAuthentikConfig();
  }, []);

  useEffect(() => {
    const fetchPublicConfig = async () => {
      try {
        const res = await axios.get('/api/config/public');
        setBackendBaseUrl(res.data?.backendBaseUrl || 'http://localhost:5000');
      } catch (err) {
        console.error('공개 설정 조회 실패:', err);
        setBackendBaseUrl('http://localhost:5000');
      }
    };

    fetchPublicConfig();
  }, []);


  const handleLoginClick = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, M_ComCode: selectedComCode };
      const res = await axios.post('/api/auth/login', payload);
      const { token, session } = res.data;
      handleLogin(token);
      sessionStorage.setItem('M_ComCode', selectedComCode);
      sessionStorage.setItem("session", session);
      navigate('/');
    } catch (err) {
      console.error('로그인 실패:', err);
      alert('아이디 또는 비밀번호가 올바르지 않습니다');
    }
  };

  const handleAuthentikLogin = useCallback(() => {
    if (!selectedComCode) {
      alert('회사를 먼저 선택해주세요.');
      return;
    }

    const next = '/';
    const query = new URLSearchParams({
      M_ComCode: selectedComCode,
      next,
    });
    window.location.href = `${backendBaseUrl}/api/auth/authentik/start?${query.toString()}`;
  }, [backendBaseUrl, selectedComCode]);

  const shouldAutoRedirectToSso =
    authentikEnabled &&
    isTrueValue(loginInfo?.M_UseSSO) &&
    isTrueValue(loginInfo?.M_RedirectSSO);

  const showAuthentikLoginButton =
    authentikEnabled &&
    isTrueValue(loginInfo?.M_UseSSO) &&
    !shouldAutoRedirectToSso;

  useEffect(() => {
    if (!selectedComCode) {
      autoRedirectedComCodeRef.current = '';
      return;
    }

    if (!shouldAutoRedirectToSso) {
      if (autoRedirectedComCodeRef.current === selectedComCode) {
        autoRedirectedComCodeRef.current = '';
      }
      return;
    }

    if (autoRedirectedComCodeRef.current === selectedComCode) return;

    autoRedirectedComCodeRef.current = selectedComCode;
    handleAuthentikLogin();
  }, [handleAuthentikLogin, selectedComCode, shouldAutoRedirectToSso]);

  return (
    <div className="login-container">
      <div className="login-card shadow">
        {/* 회사 로고 */}
        <div className="login-logo">
          {loginInfo?.L_LogoType === 'img'
            ? <img src={withCacheBuster(loginInfo?.L_LogoImgPath, imageVersion)} alt="회사 로고" width="152" height="32" />
            : <div className="login-logo-text">{loginInfo?.L_LogoText || ''}</div>}
        </div>

        <div className="login-slogan">
          {loginInfo?.L_SloganType === 'img'
            ? <img src={withCacheBuster(loginInfo?.L_SloganImgPath, imageVersion)} alt="슬로건" width="200" height="80" />
            : <span>{loginInfo?.L_SloganText || ''}</span>}
        </div>

        <form onSubmit={handleLoginClick}>
          {!domainManager && (
            <select
              className="form-select mb-3"
              value={selectedComCode}
              onChange={(e) => setSelectedComCode(e.target.value)}
            >
              {managers.map((manager) => (
                <option key={manager.M_ComCode} value={manager.M_ComCode}>
                  {manager.M_ComName}
                </option>
              ))}
            </select>
          )}
          <input
            className="form-control mb-3"
            placeholder="아이디"
            value={form.userid}
            onChange={(e) => setForm({ ...form, userid: e.target.value })}
          />
          <input
            className="form-control mb-3"
            type="password"
            placeholder="비밀번호"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <button type="submit" className="btn btn-primary w-100">
            로그인
          </button>

          {showAuthentikLoginButton && (
            <button
              type="button"
              className="btn btn-outline-light w-100 mt-2"
              onClick={handleAuthentikLogin}
            >
              Authentik SSO 로그인
            </button>
          )}
        </form>

        {isTrueValue(loginInfo?.L_UseCR) ? (
          <div className="login-cr">{loginInfo.L_CRText}</div>
        ) : null}
      </div>
    </div>
  );
}

export default Login;